import { AppBar, Box, Button, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EmptyList,
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDropDownRHF,
	EnhancedSnackbar,
	RHookFormNumberField,
	RouterLinkButton,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import {
	GET_QUERY_KEY,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
	RQ_RAMITTANCE_LIST_SCREEN_SLUG,
} from "../../common/constants";
import { IResponse, IServerError } from "../../models/common";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatAmountCommaSeperator, formatDate, formatThousandSeperatorWithTwoFixedAmount, sortDDLByLabel, useNavigateToLogout } from "../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedDrawerMenu,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminClientRoutesComponent } from "../RoleBasedLeftMenu/routes/AdminClientRoutes";
import { CRACreditService } from "../../services/CRACreditService";
import { ClientRoutesComponent } from "../RoleBasedLeftMenu/routes/ClientRoutes";
import { ClientService } from "../../services";
import { CountryService } from "../../services/common/CountryService";
import { IAppState } from "../../store/reducers";
import { IDropDownListResponse } from "../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../models";
import { IRQRamittance } from "../../models/RQRamittance";
import { PrimeDataGridWithColumnToggled } from "../PrimeDataGridWithColumnToggled";
import { RQRamittanceDataGrid } from "./RQRamittanceDataGrid";
import { RQRamittanceDataGridWithManagedBySimplePay } from "./RQRamittanceDataGrid/RQRamittanceDataGridWithManagedBySimplePay";
import { RQRamittanceService } from "../../services/RQRamittanceService";
import { RQRemittanceHeader } from "./RQRemittanceHeader"
import { SipRounded } from "@mui/icons-material";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { TaxSlipSettingsService } from "../../services/TaxSlipSettingsService";
import { GlobalVariablesService } from "../../services/GlobalVariablesService";
import FileSaver from "file-saver";

// import { CountryService } from "../../services/CountryService";

export interface IRQRamittanceSetup { }

const useRQRamittanceSavedFrequency = (
	clientId: string,
	taxYear: number,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			'rq-remittance-saved-frequency',
		],
		queryFn: (data) => {

			return RQRamittanceService.getRQRamittanceSavedFrequency(
				clientId,
				taxYear,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetRemittanceFrequencyDDL = () =>
	useQuery({
		queryKey: ["remittance-Frequency-ddl"],
		queryFn: (data) => {
			return RQRamittanceService.getRemittanceFrequencyDDL();
		},
		refetchOnWindowFocus: false,
	});

const useGetCSSTProfileDataById = (id: null | string) =>
	useQuery({
		queryKey: ["get-csst-profile", id],
		queryFn: (data) => {
			return RQRamittanceService.getCSSTProfileDataById(id);
		},
		refetchOnWindowFocus: false,
	});

const useGetHSFProfileDataByClientIdAndTaxYear = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["get-hsf-profile", clientId, taxYear],
		queryFn: (data) => {
			return RQRamittanceService.getHSFDataByClientIdAndTaxyear(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetCSSTProfileDDL = (clientId: string) =>
	useQuery({
		queryKey: ["csst-profile-ddl"],
		queryFn: (data) => {
			return RQRamittanceService.getCSSTProfileDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

const RQ_RAMITTANCE_LIST_QUERY_KEY = "rq-ramittance-list";
const useRQRamittances = (
	clientId: string,
	remittanceFrequencyId: string | undefined,
	managedBy: number,
	taxYear: number,
	// params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [
			RQ_RAMITTANCE_LIST_QUERY_KEY,
			clientId,
			remittanceFrequencyId,
			managedBy,
			taxYear,
		],
		queryFn: (data) => {

			return RQRamittanceService.getRQRamittance(
				clientId,
				remittanceFrequencyId,
				managedBy,
				taxYear,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const CLIENT_INFO_DATA_QUERY_KEY = "client-info-data-record";
const useGetClientInformationById = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [CLIENT_INFO_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: (data) => {
			return ClientService.getSimpleClientById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const GLOBAL_VARIABLES_DATA_QUERY_KEY = "global-variables-data"
const useGetGlobalVariables = (
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [GLOBAL_VARIABLES_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => {
			return GlobalVariablesService.getById(id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetCountryDDL = () =>
	useQuery({
		queryKey: ["country-ddl"],
		queryFn: (data) => {
			return CountryService.getCountryDDL();
		},
		refetchOnWindowFocus: false,
	});

export const RQRamittanceSetup: React.FC<IRQRamittanceSetup> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isAdminUser = useSelector(
		(state: IAppState) => state.user.profile.isSuperStatus
	);
	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	// const companyInformation = useSelector((state: IAppState) => state.dashboardApisData.client_detail)
	const navigateToLogout = useNavigateToLogout();
	const { AdminClientRoutes } = AdminClientRoutesComponent();
	const { ClientRoutes } = ClientRoutesComponent();
	// for delete row Purpose
	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [selectedRowsValues, setSelectedRowsValues] = useState([]);
	const [rqRamittanceSetupTableData, setRQRamittanceSetupTableData] =
		useState([]);
	const [remittanceFrequencyId, setRemittanceFrequencyId] = useState(undefined);
	const [managedBy, setManagedBy] = useState(200);
	const [selectedCSSTProfile, setSelectedCSSTProfile] = useState<any>(null);
	const { message, componentState } = useSelector(
		(state: IAppState) => state.viewState
	);
	const [openDialog, setDialogOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const [statusValue, setStatusValue] = useState("");
	const [apiAction, setApiAction] = useState("");
	const [emptyList, setEmptyList] = useState(false);
	const [searchText, setSearchText] = useState({});
	const [zeroRQRamittance, setzeroRQRamittance] = useState(true);
	const [rqRamittanceSetupUpdatedItems, setRQRamittanceSetupUpdatedItems] =
		useState<any>(undefined);
	// for delete row
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [rqRamittanceRowData, setRQRamittanceRowData] = useState<any>(0);
	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);
	const { id } = useSelector((state: IAppState) => state.accountant);
	const [quebecGlobalVariablesId, setQuebecGlobalVariablesId] = useState(undefined);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadinData, setIsLoadinData] = useState(false);

	const formMethods = useForm<any>({
		// resolver: yupResolver(ValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "firstName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "firstName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});


	// const remittanceFrequencyIdField = formMethods.getValues("remittanceFrequencyId");
	// const managedByField = formMethods.getValues("managedBy");

	// useEffect(() => {
	// 	if (remittanceFrequencyIdField && managedByField) {
	// 		setRemittanceFrequencyId(remittanceFrequencyIdField);
	// 		setManagedBy(managedByField);
	// 	}
	// }, [remittanceFrequencyIdField, managedByField]);

	const { isPending: isPendingCountryDDL, data: countryList } =
		useGetCountryDDL();

	useEffect(() => {
		if (countryList && Array.isArray(countryList)) {
			const canada = countryList.find(
				(item: any) => item.label === "Canada" || "canada"
			);
			if (canada) {
				(async () => {
					try {

						const response = await CountryService.getProvinceOrStateDDL(
							navigateToLogout,
							canada.value,
							"Province"
						);
						if (response && Array.isArray(response)) {
							const quebec = response.find(
								(item: any) => item.shortCode === "QC"
							);
							(async () => {
								try {
									setIsLoading(true);
									const response = await GlobalVariablesService.getDateEffectiveDDL(
										String(taxYear),
										quebec.value
									);
									const today = new Date();
									today.setHours(0, 0, 0, 0);
									const filtered = response.filter((item: any) => new Date(item.label) <= today);
									const latest = filtered.reduce((prev: any, curr: any) => {
										return new Date(curr.label) > new Date(prev.label) ? curr : prev;
									});
									setQuebecGlobalVariablesId(latest.value)
								} catch (err) {
									console.error("error", err);
								} finally {
									setIsLoading(false);
								}
							})();
						}
					} catch (err) {
						console.error("error", err);
					}
				})();
			}
		}
	}, [countryList]);

	const { isPending: isPendingGLobalVariablesById, data: quebecGlobalVariablesData } = useGetGlobalVariables(
		quebecGlobalVariablesId,
		navigateToLogout
	);

	const { isPending: isPendingClientInformationById, data: companyInformation } = useGetClientInformationById(
		clientIdStore,
		navigateToLogout
	);
	const { isPending: isPendingRQRamittanceSavedFrequency, data: rqRamittanceSavedFrequency }: any = useRQRamittanceSavedFrequency(
		clientIdStore,
		taxYear,
		navigateToLogout
	);

	useEffect(() => {
		// if (rqRamittanceSavedFrequency && rqRamittanceSavedFrequency.frequencies !== null) {
		if (rqRamittanceSavedFrequency) {
			formMethods.setValue(
				"remittanceFrequencyId",
				rqRamittanceSavedFrequency.frequencies
			);
			setRemittanceFrequencyId(rqRamittanceSavedFrequency.frequencies);
			formMethods.setValue(
				"managedBy",
				rqRamittanceSavedFrequency.managedBy
			);
			setManagedBy(rqRamittanceSavedFrequency.managedBy);
		}
	}, [rqRamittanceSavedFrequency]);

	const { isPending: pendingCSSTProfileDAta, data: csstProfileData } = useGetCSSTProfileDataById(
		selectedCSSTProfile
	);
	const { isPending: pendingHSFProfileDAta, data: hsfDataByClientIdndTaxYear } = useGetHSFProfileDataByClientIdAndTaxYear(
		clientIdStore, taxYear,
	);
	console.log("hsfDataByClientIdndTaxYear", hsfDataByClientIdndTaxYear)
	const valuess = formMethods.getValues();
	console.log("values", valuess)
	useEffect(() => {
		if (hsfDataByClientIdndTaxYear) {
			formMethods.setValue("totalGrossPaidInYear", hsfDataByClientIdndTaxYear.totalGrossPaidInYear)
			formMethods.setValue("businessStartedBeforeYear", Number(hsfDataByClientIdndTaxYear.businessStartedBeforeYear))
		}
	}, [hsfDataByClientIdndTaxYear])

	useEffect(() => {
		if (csstProfileData) {
			formMethods.setValue("rate", String(csstProfileData.rate))
			// const effectiveDate = new Date(csstProfileData.effectiveDate)
			// formMethods.setValue("effectiveDate", effectiveDate)
			formMethods.setValue(
				"effectiveDate",
				csstProfileData.effectiveDate ? new Date(csstProfileData.effectiveDate) : null
			);
			formMethods.setValue("savedCSSTProfiles", csstProfileData.id)
		}
	}, [csstProfileData])

	const { isPending: isPendingRemittanceFrequencyDDL, data: remittanceFrequencyDDL } = useGetRemittanceFrequencyDDL();

	const updatedRemittanceFrequencyDDL = useMemo(() => {
		if (!remittanceFrequencyDDL) return [];
		const filteredRemittanceFrequencyDDL = remittanceFrequencyDDL.filter(
			(item: any) => item.label !== "Annual"
		);
		return filteredRemittanceFrequencyDDL.map((item: any) => {
			switch (item.label) {
				case "Regular":
					return { ...item, label: "Monthly" };
				case "Threshold 1":
					return { ...item, label: "Twice Monthly" };
				case "Threshold 2":
					return { ...item, label: "Weekly" };
				default:
					return item;
			}
		});
	}, [remittanceFrequencyDDL]);

	const { isPending: isPendingCSSTDDL, data: csstDDL } = useGetCSSTProfileDDL(clientIdStore);

	const updatedcsstDDL = useMemo(() => {
		if (csstDDL) {
			if (csstDDL && Array.isArray(csstDDL)) {
				let csst: IDropDownListResponse[] = sortDDLByLabel(csstDDL).map(
					(csstItem: IDropDownListResponse) => ({
						label: csstItem.label,
						value: csstItem.value,
					})
				);
				csst.unshift({
					label: "New Rate",
					value: "new-rate",
				});
				return csst;
			}
		}
	}, [csstDDL])

	useEffect(() => {
		if (updatedcsstDDL && updatedcsstDDL.length > 0) {
			const realProfiles = updatedcsstDDL.filter((item: any) => item.value !== "new-rate");
			if (realProfiles.length > 0) {
				const latest = realProfiles.reduce((prev: any, curr: any) => {
					return new Date(curr.label) > new Date(prev.label) ? curr : prev;
				});
				if (!selectedCSSTProfile) setSelectedCSSTProfile(latest.value);
			}
		}
	}, [updatedcsstDDL]);


	const { isPending, data } = useRQRamittances(
		clientIdStore,
		remittanceFrequencyId,
		managedBy,
		taxYear,
		navigateToLogout
	); //useQuery('accountants-list', AccountantService.getAccountantList, { refetchOnWindowFocus: false });
	const rqRamittanceSetup: any | null = data;

	console.log("rqRamittanceSetuprqRamittanceSetup", rqRamittanceSetup)

	const { isPending: isSubmitting, mutate: submitRqRemittance } = useMutation({
		mutationFn: RQRamittanceService.create,

		onSuccess: (res: IRQRamittance | IServerError | IResponse | any) => {


			if (res && res.status === 200) {
				queryClient.invalidateQueries({
					queryKey: [RQ_RAMITTANCE_LIST_QUERY_KEY],
				});
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const onSubmitButtonClickedHandler = () => {
		const updatePayloadData =
			rqRamittanceSetupTableData &&
			rqRamittanceSetupTableData.map((item: any) => ({
				...item,
				chequeDate: formatDate(item.chequeDate),
			}));

		const EditedRowData = updatePayloadData.filter((item: any) => item.canEdit)
		submitRqRemittance({ data: [EditedRowData[0]] })
	};

	const { isPending: isPendingCSSTSubmit, mutate: submitRqRemittanceCSSTValues } = useMutation({
		mutationFn: RQRamittanceService.createCSST,
		onSuccess: (res: IRQRamittance | IServerError | IResponse | any) => {
			if (res && res.status === 201) {
				// queryClient.invalidateQueries({
				// 	queryKey: ["csst-profile-ddl"],
				// });
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				setSelectedCSSTProfile(res.data.id)
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: ["csst-profile-ddl"],
					}),
					queryClient.invalidateQueries({
						queryKey: [RQ_RAMITTANCE_LIST_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const onSubmitCSSTHandler = () => {
		const values = formMethods.watch();
		if (values.RQRate === null) {
			setSnackbarData({
				open: true,
				message: "Rate is required",
				severity: "error"
			})
		} else if (!values.effectiveDate || values.effectiveDate === null) {
			setSnackbarData({
				open: true,
				message: "Effective Date is required",
				severity: "error"
			})
		} else {
			let payload = {
				// id:id && id,
				// rate: "121211111",
				// effectiveDate: "2024-11-11"
				clientId: clientIdStore,
				taxYear: taxYear,
				savedCSSTProfiles: values.savedCSSTProfiles,
				rate: values.rate,
				effectiveDate: values.effectiveDate && formatDate(values.effectiveDate),
			}
			submitRqRemittanceCSSTValues(payload)
		}
	}

	const { isPending: isPendingHSFSubmit, mutate: submitRqRemittanceHSFValues } = useMutation({
		mutationFn: RQRamittanceService.createHSF,
		onSuccess: (res: IRQRamittance | IServerError | IResponse | any) => {
			if (res && res.status === 201) {
				// queryClient.invalidateQueries({
				// 	queryKey: [RQ_RAMITTANCE_LIST_QUERY_KEY],
				// });
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: ["get-hsf-profile"],
					}),
					queryClient.invalidateQueries({
						queryKey: [RQ_RAMITTANCE_LIST_QUERY_KEY],
					}),
				]);
			} else {
				setSnackbarData({
					open: true,
					message:
						res && res.message && res.message
							? res.message
							: "Something went wrong.",
					severity: "error",
				});
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const onSubmitHSFHandler = () => {
		const values = formMethods.watch();

		let hsfRate = null;
		// if (values.businessStartedBeforeYear === 200) {
		// 	if (globalSetingsRes) {

		// 		if (values.totalGrossPaidInYear <= globalSetingsRes.qhsfPrimaryAndManufacturing.minimumQhsfPrimaryManufacture) {
		// 			hsfRate = globalSetingsRes.qhsfPrimaryAndManufacturing.rate1QhsfPrimaryManufacture
		// 		} else if (
		// 			values.totalGrossPaidInYear > globalSetingsRes.qhsfPrimaryAndManufacturing.minimumQhsfPrimaryManufacture &&
		// 			values.totalGrossPaidInYear < globalSetingsRes.qhsfPrimaryAndManufacturing.maximumQhsfPrimaryManufacture
		// 		) {
		// 			let resultValue =
		// 				globalSetingsRes.qhsfPrimaryValue1 +
		// 				(globalSetingsRes.qhsfPrimaryValue2 * values.totalGrossPaidInYear) /
		// 				globalSetingsRes.qhsfPrimaryAndManufacturing.normalizeQhsfPrimaryManufacture;
		// 			let rountValue = (Math.round(resultValue * 100) / 100).toFixed(2);
		// 			hsfRate = rountValue
		// 		} else if (values.totalGrossPaidInYear >= globalSetingsRes.qhsfPrimaryAndManufacturing.maximumQhsfPrimaryManufacture) {
		// 			hsfRate = globalSetingsRes.qhsfPrimaryAndManufacturing.rate2QhsfPrimaryManufacture
		// 		}
		// 	}
		// } else {
		// 	if (globalSetingsRes) {

		// 		if (values.totalGrossPaidInYear <= globalSetingsRes.qhsf.minimumQhsf) {
		// 			hsfRate = globalSetingsRes.qhsf.rate1Qhsf
		// 		} else if (
		// 			values.totalGrossPaidInYear > globalSetingsRes.qhsf.minimumQhsf &&
		// 			values.totalGrossPaidInYear < globalSetingsRes.qhsf.maximumQhsf
		// 		) {
		// 			let resultValue =
		// 				globalSetingsRes.qhsfValue1 +
		// 				(globalSetingsRes.qhsfValue2 * values.totalGrossPaidInYear) /
		// 				globalSetingsRes.qhsf.normalizeQhsf;
		// 			let rountValue = (Math.round(resultValue * 100) / 100).toFixed(2);
		// 			hsfRate = rountValue
		// 		} else if (values.totalGrossPaidInYear >= globalSetingsRes.qhsf.maximumQhsf) {
		// 			hsfRate = globalSetingsRes.qhsfPrimaryAndManufacturing.rate2QhsfPrimaryManufacture
		// 		}
		// 	}

		// here we dont have rate basically rate is value and rate is missing and have to shw fized value for now 
		if (values.businessStartedBeforeYear === 200) {
			if (quebecGlobalVariablesData) {

				if (values.totalGrossPaidInYear <= quebecGlobalVariablesData.qhsfPrimaryAndManufacturing.minimumQhsfPrimaryManufacture) {
					hsfRate = 1.26
				} else if (
					values.totalGrossPaidInYear > quebecGlobalVariablesData.qhsfPrimaryAndManufacturing.minimumQhsfPrimaryManufacture &&
					values.totalGrossPaidInYear < quebecGlobalVariablesData.qhsfPrimaryAndManufacturing.maximumQhsfPrimaryManufacture
				) {
					let resultValue =
						quebecGlobalVariablesData.qhsfPrimaryAndManufacturing.rate1QhsfPrimaryManufacture +
						(quebecGlobalVariablesData.qhsfPrimaryAndManufacturing.rate2QhsfPrimaryManufacture * values.totalGrossPaidInYear) /
						quebecGlobalVariablesData.qhsfPrimaryAndManufacturing.normalizeQhsfPrimaryManufacture;
					let rountValue = (Math.round(resultValue * 100) / 100).toFixed(2);
					hsfRate = rountValue
				} else if (values.totalGrossPaidInYear >= quebecGlobalVariablesData.qhsfPrimaryAndManufacturing.maximumQhsfPrimaryManufacture) {
					hsfRate = 4.26
				}
			}
		} else {
			if (quebecGlobalVariablesData) {

				if (values.totalGrossPaidInYear <= quebecGlobalVariablesData.qhsf.minimumQhsf) {
					hsfRate = 1.65
				} else if (
					values.totalGrossPaidInYear > quebecGlobalVariablesData.qhsf.minimumQhsf &&
					values.totalGrossPaidInYear < quebecGlobalVariablesData.qhsf.maximumQhsf
				) {
					let resultValue =
						quebecGlobalVariablesData.qhsf.rate1Qhsf +
						(quebecGlobalVariablesData.qhsf.rate2Qhsf * values.totalGrossPaidInYear) /
						quebecGlobalVariablesData.qhsf.normalizeQhsf;
					let rountValue = (Math.round(resultValue * 100) / 100).toFixed(2);
					hsfRate = rountValue
				} else if (values.totalGrossPaidInYear >= quebecGlobalVariablesData.qhsf.maximumQhsf) {
					hsfRate = 4.26
				}
			}
		}
		let payload = {
			clientId: clientIdStore,
			taxYear: taxYear,
			businessStartedBeforeYear: values.businessStartedBeforeYear,
			totalGrossPaidInYear: values.totalGrossPaidInYear,
			hsfRate: Number(hsfRate)
		}
		console.log("payloadpayloadpayloadpayload", payload)
		console.log("hsfRate", hsfRate)
		submitRqRemittanceHSFValues(payload);
	}

	const upadtedRQRamittanceSetupItems = useMemo(() => {
		if (rqRamittanceSetup && rqRamittanceSetup.items) {
			const items = [...rqRamittanceSetup.items];

			// Find the last item with a non-null remitAmount using binary search
			let left = 0;
			let right = items.length - 1;
			let lastRemittedIndex = -1;

			while (left <= right) {
				let mid = Math.floor((left + right) / 2);
				if (items[mid].remitAmount != null) {
					lastRemittedIndex = mid;
					left = mid + 1; // look for later remitted value
				} else {
					right = mid - 1;
				}
			}

			// Find the first null remitAmount AFTER the last remitted one
			let nextNullIndex = -1;
			for (let i = lastRemittedIndex + 1; i < items.length; i++) {
				if (items[i].remitAmount == null) {
					nextNullIndex = i;
					break;
				}
			}
			// Map and add the two tags
			return items.map((item, index) => ({
				...item,
				// canDelete:
				// 	items[0].remitAmount === null ? false : index === lastRemittedIndex,
				// canEdit: items[0].remitAmount === null ? true : index === nextNullIndex,
				canDelete: index === lastRemittedIndex,
				canEdit: index === nextNullIndex,
				// csstAmount: formatAmountCommaSeperator(item.csstAmount),
				// hsfAmount: formatAmountCommaSeperator(item.hsfAmount), // add for shpw values in list 
				// dueAmount: formatAmountCommaSeperator(item.dueAmount),// add for shpw values in list 
			}));
		}
	}, [rqRamittanceSetup]);

	const { isPending: isDeleting, mutate: deleteRQRamittanceById } =
		useMutation({
			mutationFn: RQRamittanceService.delRQRemittance,

			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the RQRemittance fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					queryClient.invalidateQueries({
						queryKey: ["csst-profile-ddl"],
					});
					formMethods.setValue("rate", null)
					formMethods.setValue("effectiveDate", null)
					formMethods.setValue("savedCSSTProfiles", 'new-rate')
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				return queryClient.invalidateQueries({
					queryKey: [RQ_RAMITTANCE_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteDepartmentsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				id: selectedCSSTProfile,
				navigateToLogout: navigateToLogout,
			};
			deleteRQRamittanceById(delPayload);
			setDialogOpen(false);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setRQRamittanceRowData(params);
	}, []);

	const expandedLabels = ["Taxes", "Remittance"];
	const expandedRQRamittanceSetupOrSetupItem = "RQ"; // Special case for "List" under "RQRamittance"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Remittance" &&
				item.label === expandedRQRamittanceSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedRQRamittanceSetupOrSetupItem && "/taxes/remittance/rq"
			};
			return enhancedItem;
		});
	};

	const onSavedCSSTSelectionChangeHandler = useCallback((params: any) => {
		if (params.value !== "new-rate") {
			setSelectedCSSTProfile(params.value)
		} else {
			setSelectedCSSTProfile(null);
			formMethods.setValue("rate", null)
			formMethods.setValue("effectiveDate", null);
		}

	}, []);

	const onCSSTDeleteClicked = useCallback(() => {
		setDialogOpen(true);
		setSingleDelete(true);
	}, []);

	const onRowEditClicked = useCallback((params: any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/rqRamittance/setup/${params.id}`;
		navigate(path);
	}, []);

	const onSearchButtonClicked = () => {
		setQueryParams(searchText);
		setzeroRQRamittance(false);
	};
	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const callAction = () => {
		if (apiAction === "delete") {
			alert(`Delete row with ID: ${selectedIds} has been clicked!`);
			// TODO api call for delete
		} else if (apiAction === "updateStatus") {
			alert(`action need to trigger : ${apiAction}`);
			// TODO api call for update status
		}
		setDialogOpen(false);
	};

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const selectionToolbar = useCallback((selectedItems: any) => {
		setSelectedIds(selectedItems);

		if (selectedItems.length) {
			return (
				// <Box sx={{ marginTop: theme.spacing(1) }}>
				// 	<Button
				// 		onClick={() => onRowMultipleDeleteClicked(selectedItems)}
				// 		variant="contained"
				// 		color="error"
				// 	>
				// 		Delete
				// 	</Button>
				// </Box>
				<></>
			);
		}
		return (
			// <Box sx={{ marginTop: theme.spacing(1) }}>
			// 	<RouterLinkButton to="/rqRamittance/setup" text="Add RQRamittance" />
			// </Box>
			<></>
		);
	}, []);

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: RQ_RAMITTANCE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));


		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);

	const onSubmit = (values: any) => {
		// let payload = {
		// 	id: id && id,
		// 	rate: values.rqRate,
		// 	effectiveDate: values.effectiveDate && formatDate(values.effectiveDate),
		// }
		// submitRqRemittance(payload)
		// let payloadOn = {
		// 	id: id && id,
		// 	rate: values.businessStartedBeforeYear,
		// 	effectiveDate: values.totalGrossPaidInYear,
		// }
		// submitRqRemittanceValues(payloadOn);

	};

	const onRemittanceFrequencyChangeHandler = async (selectedItem: any) => {
		if (rqRamittanceSavedFrequency && rqRamittanceSavedFrequency.frequencies === null) {
			(async () => {
				setIsLoading(true)
				try {
					const responce = await RQRamittanceService.createRqRemittanceFrequency(
						clientIdStore,
						selectedItem.value,
						managedBy,
						taxYear,
						navigateToLogout
					)
				}
				catch {

				} finally {
					queryClient.invalidateQueries({
						queryKey: [
							'rq-remittance-saved-frequency'
						],
					});
					setRemittanceFrequencyId(selectedItem.value);
					queryClient.invalidateQueries({
						queryKey: [
							RQ_RAMITTANCE_LIST_QUERY_KEY,
							clientIdStore,
							selectedItem.value,
							managedBy,
							taxYear,
						],
					});
					setIsLoading(false)
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				}
			})();
		} else if (selectedItem.value) {
			// setRemittanceFrequencyId(selectedItem.value);
			// queryClient.invalidateQueries({
			// 	queryKey: [
			// 		RQ_RAMITTANCE_LIST_QUERY_KEY,
			// 		clientIdStore,
			// 		selectedItem.value,
			// 		managedBy,
			// 		taxYear,
			// 	],
			// });
			(async () => {
				setIsLoading(true)
				try {
					const responce = await RQRamittanceService.createRqRemittanceFrequency(
						clientIdStore,
						selectedItem.value,
						managedBy,
						taxYear,
						navigateToLogout
					)

				}
				catch {

				} finally {
					setIsLoading(false)
					setRemittanceFrequencyId(selectedItem.value);
					queryClient.invalidateQueries({
						queryKey: [
							RQ_RAMITTANCE_LIST_QUERY_KEY,
							clientIdStore,
							selectedItem.value,
							managedBy,
							taxYear,
						],
					});
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				}
			})();
		}
	};
	const onManagedByChangeHandler = (selectedItem: any) => {
		// setManagedBy(selectedItem.value);
		// queryClient.invalidateQueries({
		// 	queryKey: [
		// 		RQ_RAMITTANCE_LIST_QUERY_KEY,
		// 		clientIdStore,
		// 		remittanceFrequencyId,
		// 		selectedItem.value,
		// 		taxYear,
		// 	],
		// });

		if (rqRamittanceSavedFrequency && rqRamittanceSavedFrequency.managedBy === null) {
			(async () => {
				setIsLoadinData(true)
				try {
					const responce = await RQRamittanceService.createRqRemittanceFrequency(
						clientIdStore,
						remittanceFrequencyId,
						selectedItem.value,
						taxYear,
						navigateToLogout
					)

				}
				catch {

				} finally {
					queryClient.invalidateQueries({
						queryKey: [
							'cra-remittance-saved-frequency'
						],
					});
					setManagedBy(selectedItem.value);
					queryClient.invalidateQueries({
						queryKey: [
							RQ_RAMITTANCE_LIST_QUERY_KEY,
							clientIdStore,
							remittanceFrequencyId,
							selectedItem.value,
							taxYear,
						],
					});
					setIsLoadinData(false)
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				}
			})();
		} else if (selectedItem.value) {
			(async () => {
				setIsLoadinData(true)
				try {
					const responce = await RQRamittanceService.createRqRemittanceFrequency(
						clientIdStore,
						remittanceFrequencyId,
						selectedItem.value,
						taxYear,
						navigateToLogout
					)

				}
				catch {

				} finally {
					setIsLoadinData(false)
					setManagedBy(selectedItem.value);
					queryClient.invalidateQueries({
						queryKey: [
							RQ_RAMITTANCE_LIST_QUERY_KEY,
							clientIdStore,
							remittanceFrequencyId,
							selectedItem.value,
							taxYear,
						],
					});
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				}
			})();
		}
	};

	const balanceAmount = (value: number) => {
		if (value < 0) {
			return (
				<>
					<span style={{ color: "red" }}>
						{formatThousandSeperatorWithTwoFixedAmount(value)}
					</span></>
			);
		} else {
			return (
				<span>
					{formatThousandSeperatorWithTwoFixedAmount(value)}
				</span>
			);
		}
	};


	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([
		"remittanceFrequencyId",
		"managedBy",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async () => {
		const [remittanceFrequencyId, managedBy] = values;

		try {
			setIsLoading(true);
			const response = await RQRamittanceService.exportRQRemittanceExcelReport(
				clientIdStore,
				remittanceFrequencyId,
				taxYear,
				managedBy,
			);

			if (response) {
				if (response.status === 400) {
					setSnackbarData({
						open: true,
						message: response.data.message
							? response.data.message
							: "Something went wrong.",

						severity: "error",
					});
				} else {
					const filename = `RQ-Remittance.xlsx`;
					savePdfAsFile(response, filename);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {
			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}
	};


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_rqRamittance_list.png";

	return (
		<>
			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}

				sx={{
					mt: "1rem"
				}}>
				<BackdropCircularProgress
					open={isPending || isPendingRemittanceFrequencyDDL || isSubmitting || isPendingRQRamittanceSavedFrequency || isPendingCSSTDDL || isPendingCSSTSubmit || isPendingHSFSubmit || pendingCSSTProfileDAta || isPendingClientInformationById || isDeleting || pendingHSFProfileDAta || isPendingGLobalVariablesById || isLoading || isPendingCountryDDL || isLoadinData}
					testId="rqRamittance-list"
				/>
				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={deleteDepartmentsField}
					cancelActionHandler={onCloseHandler}
					testId="departments-confirmation-dialog"
				/>
				<FormProvider {...formMethods}>
					<form onSubmit={formMethods.handleSubmit(onSubmit)}>
						<RQRemittanceHeader
							csstDDL={updatedcsstDDL ?? []}
							onManagedByChangeHandler={onManagedByChangeHandler}
							onSubmitCSSTHandler={onSubmitCSSTHandler}
							onSubmitHSFHandler={onSubmitHSFHandler}
							onRemittanceFrequencyChangeHandler={onRemittanceFrequencyChangeHandler}
							remittanceFrequencyDDL={sortDDLByLabel(updatedRemittanceFrequencyDDL)}
							onCSSTDeleteClicked={onCSSTDeleteClicked}
							onSavedCSSTSelectionChangeHandler={onSavedCSSTSelectionChangeHandler}
							rqRamittanceSavedFrequency={rqRamittanceSavedFrequency}
							rqRamittanceSetup={rqRamittanceSetup && rqRamittanceSetup}
							companyInformation={companyInformation}
							selectedCSSTProfile={selectedCSSTProfile}
							taxYear={taxYear}
							hsfDataByClientIdndTaxYear={hsfDataByClientIdndTaxYear}
							onExportButtonClick={onExportButtonClick}

						/>
					</form>
				</FormProvider>
				{managedBy !== 100 ? (
					<RQRamittanceDataGrid
						columns={rqRamittanceSetup ? rqRamittanceSetup.headerColumns : []}
						rows={
							rqRamittanceSetup && upadtedRQRamittanceSetupItems
								? upadtedRQRamittanceSetupItems
								: []
						}
						showIndexColumn={true}
						showChecboxColumn={isAdminUser === 400 ? true : false}
						// showSettingColumn={true}
						// onRowEditClicked={onRowEditClicked}
						onRowDeleteClicked={onRowDeleteClicked}
						onSnackbarMessageData={(value: any) => setSnackbarData(value)}
						totalRecords={
							rqRamittanceSetup && rqRamittanceSetup.total
								? rqRamittanceSetup.total
								: 0
						}
						initialPageSize={INITIAL_PAGE_SIZE}
						rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
						onRequestParamsChange={onRequestParamsChange}
						selectionToolbar={selectionToolbar}
						selectedRowsValues={(value: any) => setSelectedRowsValues(value)}
						searchFieldPlaceholder={"Search..."}
						changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
							setzeroRQRamittance(value)
						}
						pageTitle={"RQRamittance"}
						rqRamittanceSetupTableData={(data: any) =>
							setRQRamittanceSetupTableData(data)
						}
						tableMargin={"0"}
					/>
				) : (
					<RQRamittanceDataGridWithManagedBySimplePay
						columns={rqRamittanceSetup ? rqRamittanceSetup.headerColumns : []}
						rows={
							rqRamittanceSetup && upadtedRQRamittanceSetupItems
								? upadtedRQRamittanceSetupItems
								: []
						}
						showIndexColumn={true}
						tableMargin={"0"}
					/>
				)}

				{rqRamittanceSetup && rqRamittanceSetup.items && managedBy === 200 && (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							// backgroundColor: "#fff",
							backgroundColor: "#F7F7F7",
							// marginTop: theme.spacing(3),
							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					>
						<Toolbar>
							<EnhancedButtonPrimeReact
								label="Submit"
								// type="submit"
								onClickHandler={onSubmitButtonClickedHandler}
								// icon="pi pi-check"
								raised
								severity={EnhancedButtonPrimeReactSeverity.Success}
							/>
						</Toolbar>
					</AppBar>
				)}
			</Grid>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
